import React from 'react';
import { useTranslation } from 'localization';
import {
  AccessDeniedMolecule,
  SpinnerAtom,
  usePlatformAdmin,
} from 'design-system/my-lib';
import './RequireMetasphereAdmin.scss';

export interface RequireMetasphereAdminProps {
  children: React.ReactNode;
}

/**
 * Route guard that gates `{children}` on platform-admin status. Delegates the
 * check to `usePlatformAdmin()` (see `standards/frontend/permissions.md`),
 * which owns the session cache, the fail-closed error path, and the
 * `permissionClient.get('/admin/check')` call. This component stays a thin
 * four-state renderer: loading → unavailable → denied → allowed. An
 * unavailable check (the permission service could not answer) is not a
 * denial: it says the check could not be made and offers Retry.
 */
const RequireMetasphereAdmin: React.FC<RequireMetasphereAdminProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { isPlatformAdmin, loading, unavailable, retry } = usePlatformAdmin();

  if (loading) {
    return (
      <div
        className="require-metasphere-admin__loading"
        role="status"
        aria-live="polite"
      >
        <SpinnerAtom size="large" label={t('admin.guard.checking')} />
      </div>
    );
  }

  if (unavailable) {
    return (
      <AccessDeniedMolecule
        title={t('admin.guard.unavailable.title')}
        message={t('admin.guard.unavailable.message')}
        iconClassName="pi pi-exclamation-triangle"
        onRetry={retry}
      />
    );
  }

  if (!isPlatformAdmin) {
    return <AccessDeniedMolecule />;
  }

  return <>{children}</>;
};

export default RequireMetasphereAdmin;
